import { Emitter } from '../util/emitter'

export type TrackingStatus = 'searching' | 'tracking' | 'lost'

export interface TrackingSnapshot {
  status: TrackingStatus
  /** 0..1 quality estimate supplied by the tracker (size / confidence based). */
  quality: number
  /** True while the target is missing but we are still within the hold window (keep last pose). */
  holding: boolean
  /** ms since the last successful detection. */
  sinceDetectionMs: number
}

export interface TrackingStateOptions {
  /** Consecutive detections required before reporting `tracking` (avoids one-frame flickers). */
  foundAfterFrames: number
  /** Time without detection after which we hold the last pose before reporting `lost`. */
  holdMs: number
  /** After being lost for this long, go back to `searching` (UI: "show your face"). */
  searchAfterMs: number
  /** Minimum quality to count as a valid detection. */
  minQuality: number
}

export const TRACKING_STATE_DEFAULTS: TrackingStateOptions = {
  foundAfterFrames: 2,
  holdMs: 250,
  searchAfterMs: 1500,
  minQuality: 0,
}

export interface TrackingStateEvents extends Record<string, unknown> {
  change: TrackingSnapshot
  found: TrackingSnapshot
  lost: TrackingSnapshot
}

/**
 * Hysteresis state machine for target presence. Feed it every frame.
 * Prevents pop-in/pop-out from single-frame detector dropouts.
 */
export class TrackingState extends Emitter<TrackingStateEvents> {
  readonly options: TrackingStateOptions
  private status: TrackingStatus = 'searching'
  private consecutive = 0
  private lastDetectionT: number | null = null
  private lastT = 0
  private quality = 0

  constructor(options: Partial<TrackingStateOptions> = {}) {
    super()
    this.options = { ...TRACKING_STATE_DEFAULTS, ...options }
  }

  get current(): TrackingSnapshot {
    return this.snapshot()
  }

  /**
   * @param detected whether the tracker found the target this frame
   * @param quality 0..1
   * @param tMs frame timestamp (ms)
   */
  update(detected: boolean, quality: number, tMs: number): TrackingSnapshot {
    this.lastT = tMs
    const valid = detected && quality >= this.options.minQuality
    const prev = this.status
    if (valid) {
      this.consecutive++
      this.lastDetectionT = tMs
      this.quality = quality
      if (this.status !== 'tracking' && this.consecutive >= this.options.foundAfterFrames) {
        this.status = 'tracking'
      }
    } else {
      this.consecutive = 0
      const since = this.lastDetectionT === null ? Infinity : tMs - this.lastDetectionT
      if (this.status === 'tracking' && since > this.options.holdMs) this.status = 'lost'
      if (this.status === 'lost' && since > this.options.searchAfterMs) this.status = 'searching'
      if (this.status !== 'tracking') this.quality = 0
    }
    const snap = this.snapshot()
    if (prev !== this.status) {
      this.emit('change', snap)
      if (this.status === 'tracking') this.emit('found', snap)
      else if (prev === 'tracking') this.emit('lost', snap)
    }
    return snap
  }

  private snapshot(): TrackingSnapshot {
    const since = this.lastDetectionT === null ? Infinity : this.lastT - this.lastDetectionT
    return {
      status: this.status,
      quality: this.quality,
      holding: this.status === 'tracking' && since > 0 && since <= this.options.holdMs,
      sinceDetectionMs: since,
    }
  }

  reset(): void {
    this.status = 'searching'
    this.consecutive = 0
    this.lastDetectionT = null
    this.quality = 0
  }
}
